import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pause, Play, SkipForward, SkipBack, X, Mic, MicOff } from "lucide-react";
import { useDemoMode } from "@/contexts/DemoModeContext";
import Spotlight from "@/components/Spotlight";

export default function DemoOverlay() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const {
    isActive, countdown, currentScene, totalScenes, scene, elapsed, paused,
    ttsEnabled, setTtsEnabled, stop, skipNext, skipPrev, togglePause,
  } = useDemoMode();

  // Currently visible spotlights — array of { id, selector, label, durationMs }
  const [spotlights, setSpotlights] = useState([]);
  // Ref (not state) so updating it doesn't re-run the effect & cancel its own timeouts
  const scheduledSceneRef = useRef(-1);

  // ---- Schedule highlights when scene changes ----
  useEffect(() => {
    if (!isActive || !scene || paused) return undefined;
    if (scheduledSceneRef.current === currentScene) return undefined;
    scheduledSceneRef.current = currentScene;
    setSpotlights([]); // reset previous

    const hi = scene.highlights || [];
    const timeouts = hi.map((h, idx) => {
      const id = `${currentScene}-${idx}-${Date.now()}`;
      return setTimeout(() => {
        const labelText = typeof h.label === "object" ? h.label[lang] : h.label;
        setSpotlights((cur) => [
          ...cur,
          { id, selector: h.selector, label: labelText, durationMs: h.durationMs || 3000 },
        ]);
      }, h.delayMs || 0);
    });

    return () => timeouts.forEach((to) => clearTimeout(to));
  }, [isActive, scene, currentScene, paused, lang]);

  // Reset scheduledScene ref when demo turns off
  useEffect(() => {
    if (!isActive) scheduledSceneRef.current = -1;
  }, [isActive]);

  const removeSpotlight = (id) => setSpotlights((cur) => cur.filter((s) => s.id !== id));

  // ESC to exit
  useEffect(() => {
    if (!isActive && countdown === 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") stop();
      if (e.key === " ") { e.preventDefault(); if (isActive) togglePause(); }
      if (e.key === "ArrowRight" && isActive) skipNext();
      if (e.key === "ArrowLeft" && isActive) skipPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isActive, countdown, stop, togglePause, skipNext, skipPrev]);

  // -------- PRE-ROLL COUNTDOWN --------
  if (countdown > 0) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm"
        data-testid="demo-countdown"
      >
        <div className="text-center">
          <div className="text-[12px] uppercase tracking-[0.3em] text-emerald-400 font-semibold mb-6">
            {t("demo.recorder_hint")}
          </div>
          <div
            key={countdown}
            className="text-[140px] font-bold text-white leading-none"
            style={{ animation: "countdown-pop 1s ease-out" }}
          >
            {countdown}
          </div>
          <button
            onClick={stop}
            className="mt-10 text-xs text-slate-300 hover:text-white underline underline-offset-4"
            data-testid="demo-cancel-countdown"
          >
            {t("demo.cancel")}
          </button>
        </div>
        <style>{`
          @keyframes countdown-pop {
            0%   { transform: scale(0.6); opacity: 0; }
            40%  { transform: scale(1.15); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  if (!isActive || !scene) return null;

  const pct = Math.min(100, (elapsed / scene.duration) * 100);

  return (
    <>
      {/* Recording indicator (top right, doesn't interfere with capture) */}
      <div
        className="fixed top-4 right-4 z-[9998] flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/95 text-white text-[11px] font-semibold shadow-lg backdrop-blur-sm"
        data-testid="demo-rec-pill"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
        </span>
        REC · {currentScene + 1}/{totalScenes}
      </div>

      {/* Highlight spotlights for the current scene */}
      {spotlights.map((s) => (
        <Spotlight
          key={s.id}
          selector={s.selector}
          label={s.label}
          durationMs={s.durationMs}
          onDone={() => removeSpotlight(s.id)}
        />
      ))}

      {/* Bottom narration panel */}
      <div
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9998] w-[min(92vw,720px)] rounded-2xl bg-slate-900/95 text-white shadow-2xl backdrop-blur-md border border-slate-700/50 overflow-hidden"
        data-testid="demo-overlay"
      >
        {/* Progress bar */}
        <div className="h-0.5 bg-slate-700/60 relative">
          <div
            className="absolute top-0 left-0 h-full bg-emerald-400 transition-[width] duration-100 linear"
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="px-5 py-4">
          {/* Top row : scene info + controls */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-400">
                <span>Scène {currentScene + 1} / {totalScenes}</span>
                <span className="text-slate-500">·</span>
                <span className="text-slate-300">{scene.title[lang]}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setTtsEnabled(!ttsEnabled)}
                className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-slate-700/60 transition-colors"
                title={ttsEnabled ? t("demo.tts_disable") : t("demo.tts_enable")}
                data-testid="demo-tts-toggle"
              >
                {ttsEnabled ? (
                  <Mic className="h-3.5 w-3.5 text-emerald-400" />
                ) : (
                  <MicOff className="h-3.5 w-3.5 text-slate-400" />
                )}
              </button>
              <button
                onClick={skipPrev}
                className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-slate-700/60 transition-colors disabled:opacity-30"
                disabled={currentScene === 0}
                title={t("demo.prev")}
                data-testid="demo-prev"
              >
                <SkipBack className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={togglePause}
                className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-slate-700/60 transition-colors"
                title={paused ? t("demo.resume") : t("demo.pause")}
                data-testid="demo-pause"
              >
                {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              </button>
              <button
                onClick={skipNext}
                className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-slate-700/60 transition-colors disabled:opacity-30"
                disabled={currentScene >= totalScenes - 1}
                title={t("demo.next")}
                data-testid="demo-next"
              >
                <SkipForward className="h-3.5 w-3.5" />
              </button>
              <div className="w-px h-4 bg-slate-700 mx-1" />
              <button
                onClick={stop}
                className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-red-500/80 transition-colors"
                title={t("demo.exit")}
                data-testid="demo-exit"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Narration */}
          <p className="text-[15px] leading-[1.55] text-slate-100 font-medium" data-testid="demo-narration">
            {scene.narration[lang]}
          </p>
        </div>
      </div>
    </>
  );
}
